/**
 * Iter-292e — Deployment-stability "Server waking up" banner.
 *
 * Listens for the `ievntur:server-waking` custom event fired by
 * `utils/api.js` whenever a network error / 5xx response is mis-classified
 * as auth failure. The banner shows for ~6 seconds with an auto-retry
 * indicator, then disappears. If the backend comes back the user is never
 * bounced to /login — their session is preserved.
 *
 * No props required; mount once in App.js inside <ErrorBoundary>.
 */
import React, { useEffect, useState, useRef } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';

export default function ServerWakingBanner() {
  const [visible, setVisible] = useState(false);
  const [showRefresh, setShowRefresh] = useState(false);
  const dismissTimer = useRef(null);
  const refreshTimer = useRef(null);

  useEffect(() => {
    const handler = () => {
      setVisible(true);
      setShowRefresh(false);
      clearTimeout(dismissTimer.current);
      clearTimeout(refreshTimer.current);
      // Auto-dismiss after 8 s if no further failures.
      dismissTimer.current = setTimeout(() => setVisible(false), 8000);
      // Surface a manual-refresh CTA after 6 s in case the backoff retry
      // is still failing (e.g. genuinely slow deploy).
      refreshTimer.current = setTimeout(() => setShowRefresh(true), 6000);
    };
    // Iter-PO2026-06 — hide the instant connectivity is restored so the
    // banner never lingers or repeatedly interrupts normal navigation.
    const recovered = () => {
      clearTimeout(dismissTimer.current);
      clearTimeout(refreshTimer.current);
      setVisible(false);
      setShowRefresh(false);
    };
    window.addEventListener('ievntur:server-waking', handler);
    window.addEventListener('ievntur:server-recovered', recovered);
    return () => {
      window.removeEventListener('ievntur:server-waking', handler);
      window.removeEventListener('ievntur:server-recovered', recovered);
      clearTimeout(dismissTimer.current);
      clearTimeout(refreshTimer.current);
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="server-waking-banner"
      style={{
        position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)',
        zIndex: 9999, maxWidth: 'calc(100vw - 24px)',
      }}
      className="flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-900 shadow-lg"
    >
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      <span>System update in progress — reconnecting…</span>
      {showRefresh && (
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-900 px-3 py-1 text-xs font-semibold text-amber-50 hover:bg-amber-800"
          data-testid="server-waking-refresh-btn"
        >
          <RefreshCw className="h-3 w-3" /> Refresh now
        </button>
      )}
    </div>
  );
}
